import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Truck, Building2, Calendar, User, MapPin } from 'lucide-react';
import { WorkOrderStatusBadge } from '@/components/shared/StatusBadge';
import PickupStepper from '@/components/pickup/PickupStepper';
import PickupDetailsStep, { type EstimateLine } from '@/components/pickup/PickupDetailsStep';
import PickupReviewStep from '@/components/pickup/PickupReviewStep';
import { useWorkOrder, useEstimates, useCompletePickup } from '@/hooks/useWorkOrders';
import { useSites, useCategories } from '@/hooks/useSettings';
import { generatePickupPdf } from '@/utils/pickupPdfGenerator';
import { uploadPickupSummary } from '@/services/workOrderService';
import { toast } from 'sonner';

export default function WorkOrderPickupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [estimateLines, setEstimateLines] = useState<EstimateLine[]>([]);
  const [estimatesLoaded, setEstimatesLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: wo, isLoading: woLoading } = useWorkOrder(id);
  const { data: sites = [] } = useSites();
  const { data: categories = [] } = useCategories();
  const { data: existingEstimates = [] } = useEstimates(id);
  const completePickupMut = useCompletePickup();

  if (woLoading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;
  }

  if (!wo) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Work order not found.</p>
      </div>
    );
  }

  const site = sites.find((s) => s.id === wo.site_id);

  // Load existing estimates once
  if (!estimatesLoaded && existingEstimates.length > 0) {
    setEstimateLines(existingEstimates.map((e) => ({ categoryId: e.category_id, measureType: 'qty' as const, value: e.quantity })));
    setEstimatesLoaded(true);
  } else if (!estimatesLoaded) {
    setEstimatesLoaded(true);
  }

  async function handleComplete(data: { signerName: string; signature: string; timestamp: string; timezone: string }) {
    if (!wo) return;
    setIsSubmitting(true);
    try {
      // 1. Complete pickup on backend
      await completePickupMut.mutateAsync({
        id: wo.id,
        payload: {
          notes,
          signature_data: data.signature,
          signer_name: data.signerName,
          signed_at: data.timestamp,
          timezone: data.timezone,
          photos,
          estimates: estimateLines.filter((e) => e.categoryId).map((e) => ({
            category_id: e.categoryId,
            quantity: e.value,
          })),
        },
      });

      // 2. Generate PDF
      const pdfBlob = await generatePickupPdf({
        woNumber: wo.work_order_number,
        customerName: wo.customer_name ?? '',
        company: wo.company ?? '',
        siteName: site?.name ?? wo.site_id ?? '',
        scheduledDate: wo.scheduled_date ?? '',
        woType: wo.work_order_type,
        operator: wo.assigned_operator ?? '',
        estimates: estimateLines.filter((e) => e.categoryId).map((e) => ({
          category: categories.find((c) => c.id === e.categoryId)?.name ?? e.categoryId,
          value: e.value,
          unit: e.measureType === 'qty' ? 'Qty' : 'Lbs',
        })),
        notes,
        signerName: data.signerName,
        signatureDataUrl: data.signature,
        signedAt: data.timestamp,
        timezone: data.timezone,
      });

      // 3. Upload PDF for storage + email
      try {
        await uploadPickupSummary(wo.id, pdfBlob);
      } catch {
        console.warn('PDF upload failed — pickup still completed');
      }

      // 4. Offer download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pickup-summary-${wo.work_order_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Pickup completed at ${new Date(data.timestamp).toLocaleString()}`);
      navigate(`/work-orders/${wo.id}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to complete pickup');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/work-orders/${wo.id}`)} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to {wo.work_order_number}
        </Button>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground font-mono">{wo.work_order_number}</h1>
            <WorkOrderStatusBadge status={wo.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{wo.customer_name} · {wo.company}</p>
        </div>
      </div>

      <div className="max-w-3xl">
        <PickupStepper currentStep={step} />

        {/* Summary Cards — always visible */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              {wo.work_order_type === 'pickup' ? <Truck className="h-5 w-5 text-primary" /> : <Building2 className="h-5 w-5 text-primary" />}
              <div>
                <p className="text-xs text-muted-foreground">Type</p>
                <p className="text-sm font-medium capitalize text-foreground">{wo.work_order_type}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <MapPin className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Site</p>
                <p className="text-sm font-medium text-foreground">{site?.name ?? wo.site_id}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <Calendar className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Scheduled</p>
                <p className="text-sm font-medium text-foreground">{wo.scheduled_date}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <User className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Operator</p>
                <p className="text-sm font-medium text-foreground">{wo.assigned_operator || '—'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {step === 0 ? (
          <PickupDetailsStep
            categories={categories}
            estimateLines={estimateLines}
            onEstimateLinesChange={setEstimateLines}
            photos={photos}
            onPhotosChange={setPhotos}
            notes={notes}
            onNotesChange={setNotes}
            onContinue={() => setStep(1)}
          />
        ) : (
          <PickupReviewStep
            wo={wo}
            siteName={site?.name ?? wo.site_id ?? ''}
            categories={categories}
            estimateLines={estimateLines}
            photos={photos}
            notes={notes}
            onBack={() => setStep(0)}
            onComplete={handleComplete}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </>
  );
}
